/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal, Input, Radio, Empty, Space } from 'antd';
import { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

export interface MoveFolderProjectBuilderProps {
  visible: boolean;
  onClose: () => void;
  onMove: (folderId: number, newParentId: number | null) => void;
  folder: { id: number; name: string; parent_id: number | null } | null;
  allFolders: any[];
  newFolders: any[];
}

export const MoveFolderProjectBuilder: React.FC<MoveFolderProjectBuilderProps> = ({
  visible,
  onClose,
  onMove,
  folder,
  allFolders,
  newFolders,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);

  // Helper to check if a folder is a descendant of the dragged folder
  const isDescendant = (
    folderId: number,
    potentialDescendantId: number
  ): boolean => {
    if (folderId === potentialDescendantId) return true;

    const folder = allFolders.find((f) => f.id === potentialDescendantId);
    if (!folder || !folder.parent_id) return false;

    return isDescendant(folderId, folder.parent_id);
  };

  // Filter and prepare folders for display
  const availableFolders = useMemo(() => {
    if (!folder) return [];

    // Show all folders (no filtering)
    const validFolders = allFolders;

    // Build full tree structure first
    const buildTree = (parentId: number | null, level = 0): any[] => {
      return validFolders
        .filter((f) => f.parent_id === parentId)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((f) => ({
          ...f,
          level,
          children: buildTree(f.id, level + 1),
        }));
    };

    const fullTree = buildTree(null);

    // If no search query, flatten and return all
    if (!searchQuery) {
      const flattenTree = (nodes: any[]): any[] => {
        return nodes.flatMap((node) => [
          { ...node, children: undefined },
          ...flattenTree(node.children || []),
        ]);
      };
      return flattenTree(fullTree);
    }

    // Tree-aware search: show folder if it OR any descendant matches
    const searchLower = searchQuery.toLowerCase();

    const filterTree = (nodes: any[]): any[] => {
      return nodes
        .map((node) => {
          const nameMatches = node.name.toLowerCase().includes(searchLower);
          const filteredChildren = filterTree(node.children || []);
          const hasMatchingChildren = filteredChildren.length > 0;

          // Include node if it matches OR has matching children
          if (nameMatches || hasMatchingChildren) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter((node): node is NonNullable<typeof node> => node !== null);
    };

    const filteredTree = filterTree(fullTree);

    // Flatten filtered tree for display
    const flattenTree = (nodes: any[]): any[] => {
      return nodes.flatMap((node) => [
        { ...node, children: undefined },
        ...flattenTree(node.children || []),
      ]);
    };

    return flattenTree(filteredTree);
  }, [folder, allFolders, searchQuery, isDescendant]);

  const handleOk = () => {
    if (folder) {
      onMove(folder.id, selectedParentId);
      setSearchQuery('');
      setSelectedParentId(null);
    }
  };

  const handleCancel = () => {
    setSearchQuery('');
    setSelectedParentId(null);
    onClose();
  };

  // Reset selected parent when modal opens
  useMemo(() => {
    if (visible && folder) {
      setSelectedParentId(folder.parent_id);
    }
  }, [visible, folder]);

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'move-folder-pb' })}
          icon={['fas', 'arrow-alt-from-left']}
        />
      }
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={intl.formatMessage({ id: 'move' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered
      width={600}>
      <div css={styles.container}>
        <Input
          placeholder={intl.formatMessage({
            id: 'move-folder-search-placeholder',
          })}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<FontAwesomeIcon icon={['far', 'search']} />}
          css={styles.searchInput}
        />

        <div css={styles.label}>
          {intl.formatMessage({ id: 'select-target-folder' })}
        </div>

        <div
          css={styles.folderList}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleOk();
            }
          }}>
          <Radio.Group
            value={selectedParentId}
            onChange={(e) => setSelectedParentId(e.target.value)}
            css={styles.radioGroup}>
            <Space direction="vertical" css={styles.spaceContainer}>
              {/* Root level option */}
              <Radio value={null} css={styles.radioItem}>
                <div css={styles.folderItem}>
                  <FontAwesomeIcon
                    icon={['far', 'level-up-alt']}
                    css={styles.rootIcon}
                  />
                  <span css={styles.folderName}>
                    {intl.formatMessage({ id: 'make-root' })}
                  </span>
                </div>
              </Radio>

              {/* Folder list */}
              {availableFolders.length > 0 ? (
                availableFolders.map((f) => {
                  const isMovingFolderOrDescendant =
                    f.id === folder?.id ||
                    (folder && isDescendant(folder.id, f.id));

                  return isMovingFolderOrDescendant ? (
                    // No radio button for moving folder and its descendants
                    <div
                      key={f.id}
                      css={[
                        styles.folderItemWrapper,
                        styles.movingFolderWrapper,
                      ]}
                      style={{ marginLeft: `${f.level * 24 + 32}px` }}>
                      <div css={[styles.folderItem, styles.movingFolderItem]}>
                        <FontAwesomeIcon
                          icon={f.isNew ? ['far', 'folder'] : ['fas', 'folder']}
                          css={[
                            styles.folderIcon,
                            f.isNew
                              ? styles.newFolderIcon
                              : styles.existingFolderIcon,
                          ]}
                        />
                        <span css={styles.folderName}>{f.name}</span>
                      </div>
                    </div>
                  ) : (
                    <Radio key={f.id} value={f.id} css={styles.radioItem}>
                      <div
                        css={styles.folderItem}
                        style={{ marginLeft: `${f.level * 24}px` }}>
                        <FontAwesomeIcon
                          icon={f.isNew ? ['far', 'folder'] : ['fas', 'folder']}
                          css={[
                            styles.folderIcon,
                            f.isNew
                              ? styles.newFolderIcon
                              : styles.existingFolderIcon,
                          ]}
                        />
                        <span css={styles.folderName}>{f.name}</span>
                      </div>
                    </Radio>
                  );
                })
              ) : (
                <Empty
                  description={intl.formatMessage({ id: 'no-folders-yet' })}
                  css={styles.empty}
                />
              )}
            </Space>
          </Radio.Group>
        </div>
      </div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  searchInput: {
    marginBottom: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.colors.text.primary,
    marginBottom: '8px',
  },
  folderList: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: `1px solid ${theme.colors.lightGray}`,
    borderRadius: '4px',
    padding: '12px',
  },
  radioGroup: {
    width: '100%',
  },
  spaceContainer: {
    width: '100%',
  },
  radioItem: {
    width: '100%',
    marginBottom: '4px',
    '&:hover': {
      backgroundColor: theme.colors.primaryLight,
    },
  },
  folderItemWrapper: {
    width: '100%',
    marginBottom: '4px',
  },
  movingFolderWrapper: {
    backgroundColor: '#fffbe6',
    borderRadius: '4px',
    padding: '8px 12px',
    border: '1px solid #ffe58f',
  },
  movingFolderItem: {
    padding: '0 !important',
  },
  folderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px',
  },
  folderIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  newFolderIcon: {
    color: '#52c41a',
  },
  existingFolderIcon: {
    color: '#faad14',
    opacity: 0.5,
  },
  rootIcon: {
    fontSize: '16px',
    color: theme.colors.primary,
    flexShrink: 0,
  },
  folderName: {
    fontSize: '14px',
    color: theme.colors.text.primary,
  },
  empty: {
    padding: '24px',
  },
});
